import { MemoizedReactMarkdown } from '@/components/ui/markdown'
import { motion } from 'framer-motion'
import rehypeExternalLinks from 'rehype-external-links'
import remarkGfm from 'remark-gfm'
import { useTheme } from '../lib/themeContext' // Import the theme context
import posthog from 'posthog-js'

export function Message({
  message,
  direction
}: {
  message: string
  direction: string
}) {
  const { primary, accent } = useTheme() // Consume the theme context

  const phoneRegex = /\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g

  function linkifyPhoneNumbers(text: string) {
    return text.replace(phoneRegex, match => {
      const digitsOnly = match.replace(/\D/g, '')
      return `[${match}](tel:${digitsOnly})`
    })
  }

  const preprocessContent = (content: string) => {
    const partialImageMarkdownRegex = /\[(.*?)\]\((https?:\/\/[^\s\)]*)$/g

    if (typeof content !== 'string') {
      return ''
    }
    // Replace partial Markdown image links with a placeholder while streaming
    const processImage = content.replace(
      partialImageMarkdownRegex,
      (_, alt) => `Loading...`
    )
    return linkifyPhoneNumbers(processImage)
  }

  return direction === 'left' ? (
    <div
      key={message}
      className="max-w-[90%] self-start rounded-[10px] p-2.5 text-sm font-semibold"
      style={{ backgroundColor: '#F3F3F3' }}
    >
      <MemoizedReactMarkdown
        rehypePlugins={[[rehypeExternalLinks, { target: '_blank' }, remarkGfm]]}
        className="prose-sm prose-neutral prose-a:text-accent-foreground/50"
        components={{
          a: ({ href, children }) => {
            return (
              <a
                style={{ color: primary }}
                className="not-prose underline"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  posthog.capture('link_clicked', {
                    href: href,
                    linkText:
                      typeof children === 'string' ? children : 'non-text link'
                  })
                }}
              >
                {children}
              </a>
            )
          }
        }}
      >
        {preprocessContent(message)}
      </MemoizedReactMarkdown>
    </div>
  ) : (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      key={message}
      className="max-w-[90%] self-end rounded-[30px] px-4 py-2 text-sm font-semibold text-white"
      style={{ backgroundColor: primary === '#ffffff' ? accent : primary }}
    >
      <MemoizedReactMarkdown
        rehypePlugins={[[rehypeExternalLinks, { target: '_blank' }, remarkGfm]]}
        className="prose-sm prose-neutral prose-a:text-accent-foreground/50"
      >
        {message}
      </MemoizedReactMarkdown>
    </motion.div>
  )
}
